import { useCallback, useRef } from 'react';


type StreamingParserOptions<_TMessage, TChunk> = {
  parseChunk: ( chunk: TChunk ) => { content?: string; shouldUpdate: boolean; error?: string };
  onError?: ( error: Error ) => void;
  onUpdateMessage: ( content: string, isFirstChunk: boolean ) => void;
  onComplete?: () => void;
  onAbort?: () => void;
};

export const useExplorerStreamingParser = <TMessage, TChunk>( options: StreamingParserOptions<TMessage, TChunk> ) => {
  const { parseChunk, onError, onUpdateMessage, onAbort, onComplete } = options;
  const abortedRef = useRef<boolean>( false );
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>( null );

  const abort = useCallback( () => {
    abortedRef.current = true;
    if( readerRef.current ) {
      readerRef.current.cancel();
      readerRef.current = null;
    }
    if( onAbort ) {
      onAbort();
    }
  }, [ onAbort ] );

  const processStream = useCallback( async( stream: ReadableStream<Uint8Array> ) => {
    abortedRef.current = false;
    const reader = stream.getReader();
    readerRef.current = reader;
    const decoder = new TextDecoder();

    let buffer = '';
    let accumulatedText = '';
    let isFirstChunk = true;

    try {
      while( true ) {
        if( abortedRef.current ) {
          break;
        }

        const { done, value } = await reader.read();

        if( done ) {
          break;
        }

        buffer += decoder.decode( value, { stream: true } );
        const lines = buffer.split( '\n' );

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for( const line of lines ) {
          if( line.trim() ) {
            try {
              const chunk = JSON.parse( line ) as TChunk;
              const parseResult = parseChunk( chunk );

              if( parseResult.error ) {
                throw new Error( parseResult.error );
              }

              if( parseResult.shouldUpdate && parseResult.content !== undefined ) {
                accumulatedText += parseResult.content;
                onUpdateMessage( accumulatedText, isFirstChunk );
                isFirstChunk = false;
              }
            } catch( e ) {
              console.error( 'Error parsing chunk:', e, 'Line:', line );
              throw e;
            }
          }
        }
      }

      // Process any remaining data in the buffer — accumulate final chunk content
      if( buffer.trim() ) {
        try {
          const chunk = JSON.parse( buffer ) as TChunk;
          const parseResult = parseChunk( chunk );

          if( parseResult.error ) {
            throw new Error( parseResult.error );
          }

          if( parseResult.shouldUpdate && parseResult.content !== undefined ) {
            accumulatedText += parseResult.content;
            onUpdateMessage( accumulatedText, isFirstChunk );
          }
        } catch( e ) {
          console.error( 'Error parsing final chunk:', e );
          throw e;
        }
      }
    } catch( e ) {
      if( !abortedRef.current ) {
        if( onError ) {
          onError( e as Error );
        } else {
          throw e;
        }
      }
    } finally {
      readerRef.current = null;
      if( onComplete && !abortedRef.current ) {
        onComplete();
      }
    }
  }, [ parseChunk, onError, onUpdateMessage, onComplete ] );

  return { processStream, abort };
};
